import {ref} from 'vue';
import {defineStore} from 'pinia';
import {createError, useAsyncData} from '#imports';
import {readonlyState} from '@malt/utils';
import type {
    CompanyDetailsContextResponse,
    CompanyDetailsForUserResponse,
    CompanySector,
    OrganizationAndCompanies,
    SignupStateResponse,
    SignupType,
    TransitionalCompanyResponse,
    UserDetailsContextResponse,
} from '@api';
import {ClientSignupContextApi} from '@api';
import {apiConfiguration} from '~/configuration/apiConfiguration';
import {isStepAuthentication, isStepCompanyChoice, isStepCompanyDetails, isStepEmailAwaitingValidation, isStepUserDetails} from '~/lib/steps';

export const useContextStore = defineStore('context-store', () => {
    const signupContextApi = new ClientSignupContextApi(apiConfiguration);

    const email = ref('');
    const clientDepartment = ref('');
    const companyId = ref('');
    const companyName = ref('');
    const companyWebsiteUrl = ref('');
    const companyCountry = ref('');
    const companyLogoUrl = ref('');
    const companySize = ref('');
    const companyIndustry = ref('');
    const companySizes = ref<string[]>([]);
    const companyDepartments = ref<string[]>([]);
    const companySectors = ref<CompanySector[]>([]);
    const firstName = ref('');
    const isPhoneNumberMandatory = ref<boolean>(false);
    const lastName = ref('');
    const nameOfCompanyThatUserWantedToJoinButIsArchived = ref<string | undefined>(undefined);
    const organizationsAndCompanies = ref<OrganizationAndCompanies[]>([]);
    const phoneNumber = ref('');
    const redirectPath = ref<string | undefined>(undefined);
    const signupType = ref<SignupType | undefined>();
    const edrMatches = ref<boolean>();
    const transitionalCompanyId = ref<string | undefined>(undefined);

    function getCompanyIndustries(companySector: string) {
        return companySectors.value.find((cs) => cs.sector === companySector)?.industries;
    }

    function updateStoredValues(state: SignupStateResponse): void {
        if (!state) {
            return;
        }

        signupType.value = state.signupType;

        if (!state.nextStep) {
            return;
        }

        if (isStepAuthentication(state.nextStep)) {
            email.value = state.nextStep.prefilledEmail ?? '';
            redirectPath.value = state.nextStep.redirectPath;
            // When session expires we come to /email so we need to context values
            edrMatches.value = undefined;
            signupType.value = undefined;
        } else if (isStepEmailAwaitingValidation(state.nextStep)) {
            email.value = state.nextStep.email;
        } else if (isStepUserDetails(state.nextStep)) {
            firstName.value = state.nextStep.firstName ?? '';
            lastName.value = state.nextStep.lastName ?? '';
            phoneNumber.value = state.nextStep.phoneNumber ?? '';
            edrMatches.value = state.nextStep.edrMatches;
        } else if (isStepCompanyDetails(state.nextStep)) {
            companyId.value = state.nextStep.companyId || '';
            companyIndustry.value = state.nextStep.companyIndustry || '';
            companyName.value = state.nextStep.companyName || '';
            companySize.value = state.nextStep.companySize || '';
            companyWebsiteUrl.value = state.nextStep.companyWebsiteUrl || '';
            edrMatches.value = state.nextStep.edrMatches;
            nameOfCompanyThatUserWantedToJoinButIsArchived.value = state.nextStep.nameOfCompanyThatUserWantedToJoinButIsArchived;
        } else if (isStepCompanyChoice(state.nextStep)) {
            organizationsAndCompanies.value = state.nextStep.organizationsAndCompanies || [];
            edrMatches.value = state.nextStep.edrMatches;
            nameOfCompanyThatUserWantedToJoinButIsArchived.value = state.nextStep.nameOfCompanyThatUserWantedToJoinButIsArchived;
        }
    }

    async function getCompanyDetailsForUser(userIdentityId: string): Promise<void> {
        const {data, error} = await useAsyncData<CompanyDetailsForUserResponse>(`company-details-for-user-${userIdentityId}`, () =>
            signupContextApi.getCompanyDetailsForUser({
                identityId: userIdentityId,
            }),
        );

        if (error.value) {
            throw createError(error.value);
        }

        if (data.value) {
            clientDepartment.value = data.value.clientDepartment || '';
            companyId.value = data.value.companyId || '';
            companyIndustry.value = data.value.companyIndustry || '';
            companyName.value = data.value.companyName || '';
            companyWebsiteUrl.value = data.value.companyWebsiteUrl || '';
            companySize.value = data.value.companySize || '';
        }
    }

    async function getContextForCompanyDetails(): Promise<void> {
        const {data, error} = await useAsyncData<CompanyDetailsContextResponse>('company-details-context', () =>
            signupContextApi.getContextForCompanyDetails(),
        );

        if (error.value) {
            throw createError(error.value);
        }

        if (data.value) {
            companySizes.value = data.value.sizes!;
            companyDepartments.value = data.value.departments!;
            companySectors.value = data.value.sectors!;
        }
    }

    async function getContextForUserDetails(): Promise<void> {
        const {data, error} = await useAsyncData<UserDetailsContextResponse>('user-details-context', () =>
            signupContextApi.getContextForUserDetails(),
        );

        if (error.value) {
            throw createError(error.value);
        }

        if (data.value) {
            isPhoneNumberMandatory.value = data.value.phoneNumberMandatory || false;
        }
    }

    return {
        // state
        email: readonlyState(email),
        clientDepartment: readonlyState(clientDepartment),
        companyId: readonlyState(companyId),
        companyName: readonlyState(companyName),
        companyWebsiteUrl: readonlyState(companyWebsiteUrl),
        companyCountry: readonlyState(companyCountry),
        companyLogoUrl: readonlyState(companyLogoUrl),
        companySize: readonlyState(companySize),
        companyIndustry: readonlyState(companyIndustry),
        companySizes: readonlyState(companySizes),
        companyDepartments: readonlyState(companyDepartments),
        companySectors: readonlyState(companySectors),
        firstName: readonlyState(firstName),
        isPhoneNumberMandatory: readonlyState(isPhoneNumberMandatory),
        lastName: readonlyState(lastName),
        nameOfCompanyThatUserWantedToJoinButIsArchived: readonlyState(nameOfCompanyThatUserWantedToJoinButIsArchived),
        organizationsAndCompanies: readonlyState(organizationsAndCompanies),
        phoneNumber: readonlyState(phoneNumber),
        redirectPath: readonlyState(redirectPath),
        signupType: readonlyState(signupType),
        edrMatches: readonlyState(edrMatches),
        transitionalCompanyId,
        // actions
        getCompanyDetailsForUser,
        getCompanyIndustries,
        updateStoredValues,
        getContextForCompanyDetails,
        getContextForUserDetails,
    };
});
